import type { EthTransaction } from '@exodus/web3-ethereum-utils';
import pDefer from 'p-defer';

import type { Web3 } from '@/background/features/web3/module.js';
import type { ProviderResponse } from '@/background/features/web3/utils/transport.js';
import { PROVIDER_RESPONSE_MESSAGE_TYPE } from '@/background/features/web3/utils/transport.js';
import { createRpcCall } from '../../web3/utils/rpc.js';
import type { TransactionHandlerResult } from '../types.js';

type ProviderResponseBasePayload = {
  id: number;
};

type ProviderResponseResultPayload = ProviderResponseBasePayload & {
  /** Transaction ID. */
  result: string;
};

type ProviderResponseErrorPayload = ProviderResponseBasePayload & {
  error: {
    code: number;
    message: string;
    data: unknown;
  };
};

type ProviderResponsePayload =
  | ProviderResponseResultPayload
  | ProviderResponseErrorPayload;

function hasError(
  payload: Record<string, unknown>,
): payload is ProviderResponseErrorPayload {
  return !!payload.error;
}

type HandleEthereumTransactionParams = {
  transaction: EthTransaction;
  web3: Web3;
};

export async function handleEthereumTransaction({
  transaction,
  web3,
}: HandleEthereumTransactionParams): Promise<TransactionHandlerResult> {
  const deferred = pDefer<TransactionHandlerResult>();

  const handleProviderResponse = ({
    data,
    origin,
  }: MessageEvent<ProviderResponse>) => {
    if (
      origin !== window.origin ||
      data.type !== PROVIDER_RESPONSE_MESSAGE_TYPE
    ) {
      return;
    }

    const payload: ProviderResponsePayload = JSON.parse(data.input);
    if (hasError(payload)) {
      deferred.reject(payload.error);
    } else {
      deferred.resolve({
        txId: payload.result,
      });
    }

    window.removeEventListener('message', handleProviderResponse);
  };

  window.addEventListener('message', handleProviderResponse);

  try {
    await web3.handleProviderRequest(
      createRpcCall({
        method: `${transaction.chainId}_eth_sendTransaction`,
        params: [transaction],
        id: 0,
      }),
    );
  } catch (error) {
    deferred.reject(error);
    window.removeEventListener('message', handleProviderResponse);
  }

  return deferred.promise;
}
